import serializeSchemaFragmentForSentry from './serializeSchemaFragment';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JSONValue = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JSONObject = Record<string, any>;

interface DataShape {
  isPresent: boolean;
  jsType: string;
  isArray: boolean;
  keyCount?: number;
  length?: number;
}

// Never report the raw value at the offending path - it's live caller/
// emergency data (medical, location, PII in a life-safety product) -
// only its shape, plus a masked preview (see buildMaskedDataPreview) that
// keeps just the trailing 4 characters of each leaf.
const describeDataShape = (raw: unknown): DataShape => {
  const shape: DataShape = {
    isPresent: raw !== undefined && raw !== null,
    jsType: typeof raw,
    isArray: Array.isArray(raw),
  };
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    shape.keyCount = Object.keys(raw).length;
  }
  if (Array.isArray(raw)) {
    shape.length = raw.length;
  }
  return shape;
};

const DEPTH_TRUNCATED_MARKER = '[Truncated: max depth reached]';
const moreMarker = (count: number) => `… ${count} more`;

// Mirrors rtt_egress/utils.py's obfuscate_phone_number: replace everything
// but the trailing 4 characters with '*', capping total output length so a
// pathologically long value doesn't produce a wall of asterisks.
const maskExceptLastFour = (value: string, maxLength = 100): string => {
  if (value.length <= 4) {
    return value;
  }
  const lastFour = value.slice(-4);
  const maskedLength = Math.min(value.length - 4, maxLength - 4);
  const omitted = value.length - 4 - maskedLength;
  const omittedNote = omitted > 0 ? `…[+${omitted} masked]` : '';
  return `${'*'.repeat(maskedLength)}${omittedNote}${lastFour}`;
};

const maskLeaf = (value: JSONValue, maxLeafLength: number): JSONValue => {
  if (value === null || value === undefined || typeof value === 'boolean') {
    return value;
  }
  return maskExceptLastFour(String(value), maxLeafLength);
};

// Same bounded depth/key-count traversal as serializeSchemaFragment.ts's
// truncateValue, but masks every leaf instead of passing it through -
// object keys, array positions, and injected truncation markers are never
// masked, only the real leaf values are.
const buildMaskedPreviewTree = (
  value: JSONValue,
  depth: number,
  maxDepth: number,
  maxKeysPerLevel: number,
  maxLeafLength: number,
): JSONValue => {
  if (depth > maxDepth) {
    return DEPTH_TRUNCATED_MARKER;
  }

  if (Array.isArray(value)) {
    const truncated = value
      .slice(0, maxKeysPerLevel)
      .map(item =>
        buildMaskedPreviewTree(
          item,
          depth + 1,
          maxDepth,
          maxKeysPerLevel,
          maxLeafLength,
        ),
      );
    if (value.length > maxKeysPerLevel) {
      truncated.push(moreMarker(value.length - maxKeysPerLevel));
    }
    return truncated;
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    const result: Record<string, JSONValue> = {};
    keys.slice(0, maxKeysPerLevel).forEach(key => {
      result[key] = buildMaskedPreviewTree(
        value[key],
        depth + 1,
        maxDepth,
        maxKeysPerLevel,
        maxLeafLength,
      );
    });
    if (keys.length > maxKeysPerLevel) {
      result.__truncated_keys__ = moreMarker(keys.length - maxKeysPerLevel);
    }
    return result;
  }

  return maskLeaf(value, maxLeafLength);
};

interface MaskedDataPreviewOptions {
  maxDepth?: number;
  maxKeysPerLevel?: number;
  maxLeafLength?: number;
  maxStringLength?: number;
}

/**
 * Masked, structure-preserving preview of the raw data value at the
 * offending path, for Sentry `extra`. Object keys, array positions, and
 * nesting are untouched; every leaf string/number is masked to its real
 * last 4 characters via maskExceptLastFour. Returns undefined for
 * undefined/null raw values (dataShape's isPresent already covers that).
 */
const buildMaskedDataPreview = (
  raw: unknown,
  {
    maxDepth = 4,
    maxKeysPerLevel = 20,
    maxLeafLength = 100,
    maxStringLength = 2000,
  }: MaskedDataPreviewOptions = {},
): string | undefined => {
  if (raw === undefined || raw === null) {
    return undefined;
  }

  const masked = buildMaskedPreviewTree(
    raw,
    0,
    maxDepth,
    maxKeysPerLevel,
    maxLeafLength,
  );
  const serialized = JSON.stringify(masked);

  if (serialized.length <= maxStringLength) {
    return serialized;
  }

  return `${serialized.slice(0, maxStringLength)}…[truncated]`;
};

const withoutNullish = (
  obj: Record<string, string | undefined>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== undefined && value !== null,
    ),
  ) as Record<string, string>;

interface SchemaMismatchOwnProps {
  source?: string;
  displaySchemaUrl?: string;
  dataCategory?: string;
  dataSuffix?: string;
}

interface BuildSchemaMismatchReportParams {
  // The (usually undefined) declared `type`.
  type: unknown;
  // The offending JSON Schema fragment.
  property?: JSONObject;
  // The property's path within the schema tree.
  path: (string | number)[];
  // The root display schema.
  displaySchema?: JSONObject;
  // Props threaded through to the renderer (source, displaySchemaUrl,
  // dataCategory, dataSuffix).
  ownProps?: SchemaMismatchOwnProps;
  // The raw data value at `path` - only its shape and a masked preview
  // (last 4 real characters per leaf) are ever reported, never the value
  // itself.
  data?: unknown;
  // Reported under the `ellington.errorType` tag, distinguishing which
  // mismatch fired.
  errorType?: string;
  // First fingerprint segment, so the two mismatch types group separately.
  fingerprintPrefix?: string;
}

interface SchemaMismatchReport {
  extra: JSONObject;
  fingerprint: string[];
  tags: Record<string, string>;
}

/**
 * Builds the extra/fingerprint/tags payload reported for a schema/data
 * mismatch (SchemaTypeMismatchError or DataToSchemaTypeMismatchError), so
 * callers can pinpoint exactly which partner schema and property triggered
 * it instead of everything grouping into one Sentry issue by shared stack
 * trace.
 */
const buildSchemaMismatchReport = ({
  type,
  property,
  path,
  displaySchema,
  ownProps = {},
  data,
  errorType = 'SchemaTypeMismatch',
  fingerprintPrefix = 'ellington-schema-type-mismatch',
}: BuildSchemaMismatchReportParams): SchemaMismatchReport => {
  const pathString = path.join('.');
  const schemaIdentifier =
    ownProps.displaySchemaUrl || displaySchema?.title || 'unknown-schema';
  // Prefer the stable per-partner/flow identifier over schemaIdentifier,
  // which embeds displaySchemaUrl's content hash - that hash changes on
  // every schema republish, fragmenting Sentry issues per-deploy instead
  // of per-broken-property/flow.
  const fingerprintIdentifier = ownProps.source || schemaIdentifier;

  const extra: JSONObject = {
    type: type || 'undefined', // make sure to record when type is undefined.
    title: property?.title,
    schemaTitle: displaySchema?.title,
    path: pathString,
    propertyKeys: Object.keys(property ?? {}),
    propertySchema: serializeSchemaFragmentForSentry(property),
    dataShape: describeDataShape(data),
    maskedDataPreview: buildMaskedDataPreview(data),
    source: ownProps.source,
    displaySchemaUrl: ownProps.displaySchemaUrl,
    dataCategory: ownProps.dataCategory,
    dataSuffix: ownProps.dataSuffix,
  };

  const fingerprint = [fingerprintPrefix, fingerprintIdentifier, pathString];

  // Keep tags low-cardinality/searchable - never the full path or any id
  // (displaySchemaUrl, dataPath, selectedCallerID), which belong in `extra`.
  const tags = withoutNullish({
    'ellington.errorType': errorType,
    'ellington.schemaTitle': displaySchema?.title,
    'ellington.source': ownProps.source,
    'ellington.pathDepth': String(path.length),
  });

  return { extra, fingerprint, tags };
};

export default buildSchemaMismatchReport;
